import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";
import { changePassword } from "../api/auth";
import { errorMessage, getToken } from "../api/client";

interface SetPasswordState {
  currentPassword?: string;
}

/**
 * Shown right after a temp-password ("must change password") login. The restricted
 * token is already in localStorage; the just-typed temporary password arrives via
 * router state. On success we exchange the restricted token for a full one and the
 * user becomes authenticated.
 */
export function SetPasswordPage() {
  const { t } = useTranslation();
  const { completePasswordChange } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPassword = (location.state as SetPasswordState | null)?.currentPassword;

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Without the carried temp password (e.g. a hard refresh) or a token there's
  // nothing to do here — send them back to log in.
  useEffect(() => {
    if (!currentPassword || !getToken()) {
      navigate("/login", { replace: true });
    }
  }, [currentPassword, navigate]);

  const mismatch = confirm.length > 0 && newPassword !== confirm;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) return;
    if (newPassword !== confirm) {
      toast.error(t("setPassword.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      completePasswordChange(res);
      toast.success(t("auth.signedIn"));
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <form className="auth-card stack" onSubmit={onSubmit}>
        <h1>{t("setPassword.title")}</h1>
        <p className="muted" style={{ textAlign: "center", marginTop: "-8px" }}>
          {t("setPassword.subtitle")}
        </p>
        <TextField
          label={t("setPassword.newPassword")}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t("auth.newPasswordPlaceholder")}
          hint={t("auth.passwordHint")}
        />
        <TextField
          label={t("setPassword.confirmPassword")}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={t("auth.passwordPlaceholder")}
          error={mismatch ? t("setPassword.mismatch") : null}
        />
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={newPassword.length < 8 || newPassword !== confirm}
        >
          {t("setPassword.submit")}
        </Button>
      </form>
    </div>
  );
}
